import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import {
  fetchTimeEntries,
  fetchVacations, createVacation, updateVacation, deleteVacation,
  fetchHolidays,
  fetchRoadmapItems,
  createTimeEntry, deleteTimeEntry,
  createPhase, updatePhase, deletePhase,
  fetchProjects,
  getWsUrl, getWsToken,
} from "../api";
import { pad2, formatISODate, addDays, combineDateTime, fmtDateDE, parseISODateLocal, isoAddDays, useToday } from "../lib/dates";
import ConfirmDialog from "./ConfirmDialog";
import CustomSelect from "./CustomSelect";
import DatePicker from "./DatePicker";
import MiniCalendar from "./MiniCalendar";
import { HoursAccountYearView } from "./Time";
import {
  sickCreditMs, dailyTargetMs, accountBalanceMs,
  buildHolidayEntries, buildVacationEntries, countedVacDays,
  activePhaseOf, scopeUserToPhase, scopeEntriesToPhase, clipVacationsToPhase, vacationAccountForPhase,
} from "../lib/timeMath";
import "./AreaView.css"; // re-use area-color-popover / area-color-hex-input
import "./EmployeeAdminPage.css";

function defaultAbbreviation(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

// Day count for the "Tage" columns: rounded to one decimal, whole numbers plain,
// otherwise a German comma (6.04 → "6", 0.5 → "0,5").
function fmtDays(days) {
  const r = Math.round(days * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1).replace(".", ",");
}
// Plain day count with a DOT decimal (used for the header totals top-right).
function fmtNumDot(n) {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

// A Krankheit entry is stored as a block of the day's Soll (half day = half of it),
// so "is this a half day?" must be measured against THAT day's Soll — a werkstudent's
// full day can be as short as 4 h, which a fixed 5 h threshold would wrongly halve
// (and halve again on the next save).
function isHalfSickEntry(user, e) {
  if (!e?.started_at || !e?.ended_at) return false;
  const start = new Date(e.started_at);
  const full = dailyTargetMs(user, start);
  // No Soll that day (override 0, non-workday, outside every phase): treat it as a
  // full day. A fixed-hours fallback would re-introduce the halving bug, because a
  // werkstudent's full day is only 4 h.
  if (full <= 0) return false;
  return (new Date(e.ended_at) - start) < full * 0.75;
}

function lastNameOf(name) {
  const parts = String(name || "").trim().split(/\s+/);
  return parts[parts.length - 1] || "";
}

export default function EmployeeAdminPage({ users, onCreateUser, onUpdateUser, onDeleteUser }) {
  // Reactive today — rolls over after midnight / on tab focus without a reload.
  const todayIso = useToday();
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingPerson, setEditingPerson] = useState(false);
  const [tab, setTab] = useState("stunden");
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // person being archived (asks for leave date)
  // null = follow the default phase (the one active today, else the newest).
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [phaseEditor, setPhaseEditor] = useState(null); // { phase } — phase null = create

  // data for Urlaub + Stundenkonto tabs (admin loads all)
  // PE1: only the selected person's time entries are loaded (not everyone's).
  const [userEntries, setUserEntries] = useState([]);
  const [entriesUserId, setEntriesUserId] = useState(null); // which person userEntries belong to
  const [allVacations, setAllVacations] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [holidays, setHolidays] = useState({});
  // First-paint gate — see Time.js: hold the view until entries/vacations/roadmap
  // and holidays are all loaded so the tile numbers don't render empty then jump.
  const [ready, setReady] = useState(false);
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);

  // "Semesterferien" roadmap spans → weeks where werkstudenten may work more.
  const semesterBreaks = useMemo(
    () => (roadmapItems || [])
      .filter((it) => it.title === "Semesterferien")
      .map((it) => ({ start_year: it.start_year, start_week: it.start_week, end_year: it.end_year, end_week: it.end_week })),
    [roadmapItems]
  );

  // Admin first; active people next; archived at the bottom
  const sortedUsers = useMemo(() => {
    const admins = [];
    const active = [];
    const archived = [];
    for (const u of users || []) {
      if (u.archived) archived.push(u);
      else if (u.role === "admin") admins.push(u);
      else active.push(u);
    }
    const cmp = (a, b) => {
      const la = lastNameOf(a.username);
      const lb = lastNameOf(b.username);
      const c = la.localeCompare(lb, "de");
      if (c !== 0) return c;
      return a.username.localeCompare(b.username, "de");
    };
    admins.sort(cmp);
    active.sort(cmp);
    archived.sort(cmp);
    return { admins, active, archived };
  }, [users]);
  const flatUsers = useMemo(() => [...sortedUsers.admins, ...sortedUsers.active, ...sortedUsers.archived], [sortedUsers]);

  // People with an open request — for the sidebar badges. One Set instead of an
  // allVacations.some() per person per render (renders happen on every broadcast).
  const pendingVacationUserIds = useMemo(() => {
    const s = new Set();
    for (const v of allVacations) if (v.status === "requested") s.add(v.user_id);
    return s;
  }, [allVacations]);

  // auto-select first user when list arrives
  useEffect(() => {
    if (selectedId == null && flatUsers.length > 0) {
      setSelectedId(flatUsers[0].id);
    }
  }, [flatUsers, selectedId]);

  // Loads only the selected person's entries (see PE1). Called on selection change,
  // after edits (onChanged), and on live updates.
  // Monotonic request counter: quickly switching persons fires overlapping
  // fetches, and a LATE response for the old person used to overwrite the new
  // one — entriesUserId then never matched selectedId and the detail view hung
  // on "Laden…" until the next live update. Only the newest request may land.
  const entriesReqSeq = useRef(0);
  const [entriesError, setEntriesError] = useState(false);
  const loadEntries = useCallback(async () => {
    if (selectedId == null) { setUserEntries([]); setEntriesUserId(null); return; }
    const seq = ++entriesReqSeq.current;
    setEntriesError(false);
    try {
      const data = await fetchTimeEntries(selectedId);
      if (seq !== entriesReqSeq.current) return; // superseded by a newer request
      setUserEntries(data);
      setEntriesUserId(selectedId);
    } catch (e) {
      // Swallowing this silently left the detail view stuck on "Laden…" until
      // the next live update — show it and offer a retry instead.
      if (seq === entriesReqSeq.current) setEntriesError(true);
    }
  }, [selectedId]);
  // Same latest-wins guard as loadEntries: overlapping WS-triggered reloads can
  // resolve out of order and an older answer would revert a just-approved status.
  const vacReqSeq = useRef(0);
  const loadVacations = useCallback(async () => {
    const seq = ++vacReqSeq.current;
    try {
      const data = await fetchVacations();
      if (seq === vacReqSeq.current) setAllVacations(data);
    } catch (e) { /* ignore */ }
  }, []);
  const loadRoadmap = useCallback(async () => {
    try { setRoadmapItems(await fetchRoadmapItems()); } catch (e) { /* ignore */ }
  }, []);
  // Nur für die Namen im KW-Detail (Projekt-Aufschlüsselung) — archivierte
  // Projekte inklusive, sonst fehlt bei alten Einträgen der Name.
  const [projects, setProjects] = useState([]);
  useEffect(() => { fetchProjects(true).then(setProjects).catch(() => {}); }, []);

  // Global data (vacations for badges, roadmap) loads once; entries load per person.
  useEffect(() => {
    Promise.all([loadVacations(), loadRoadmap()]).finally(() => setReady(true));
  }, [loadVacations, loadRoadmap]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // First year that needs holiday data — derived from the people, because the
  // Stundenkonto counts the Soll from the first tracked week onward and a fixed
  // window would drop the holiday credit of older years while the Soll keeps
  // counting. /api/holidays/{year} computes any year, so there is no upper bound.
  const firstHolidayYear = useMemo(() => {
    const cur = new Date().getFullYear();
    let min = cur;
    const take = (y) => { if (Number.isFinite(y) && y >= 1900 && y < min) min = y; };
    for (const u of users || []) {
      if (u.employed_since) take(Number(String(u.employed_since).slice(0, 4)));
      for (const p of u.phases || []) take(Number(String(p.start_date || "").slice(0, 4)));
    }
    return Math.max(min, cur - 25); // hard floor so bad data can't flood the API
  }, [users]);

  // Fires once per year-range change (not per render / person switch).
  const hasUsers = (users || []).length > 0;
  useEffect(() => {
    if (!hasUsers) return; // the window depends on the people list — wait for it
    const last = new Date().getFullYear() + 1;
    const years = [];
    for (let y = firstHolidayYear; y <= last; y++) years.push(y);
    Promise.all(years.map((yr) => fetchHolidays(yr).catch(() => ({}))))
      .then((results) => {
        const merged = {};
        results.forEach((r) => Object.assign(merged, r));
        setHolidays(merged);
      })
      .catch(() => {})
      .finally(() => setHolidaysLoaded(true));
  }, [firstHolidayYear, hasUsers]);

  // The socket must survive person switches: loadEntries changes identity with the
  // selection, so the handlers go through refs and the effect runs exactly once.
  const loadEntriesRef = useRef(loadEntries);
  const loadVacationsRef = useRef(loadVacations);
  useEffect(() => {
    loadEntriesRef.current = loadEntries;
    loadVacationsRef.current = loadVacations;
  }, [loadEntries, loadVacations]);
  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        const token = getWsToken();
        if (token) ws.send(JSON.stringify({ token }));
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "workforce") { loadEntriesRef.current(); loadVacationsRef.current(); }
        } catch {}
      };
      // disposed guard: the cleanup's ws.close() fires onclose asynchronously —
      // without it every unmount left a zombie socket reconnecting forever.
      ws.onclose = () => { if (!disposed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };
    let disposed = false;
    connect();
    return () => { disposed = true; clearTimeout(reconnectTimer); if (ws) ws.close(); };
  }, []);

  // Phase edits go straight to the phase endpoints; the `users` prop only follows
  // once App.js reloads it from the "users" WebSocket broadcast, which can lag or
  // not arrive at all — the tiles above would then keep showing the old Urlaubs-
  // anspruch until a page reload. Those endpoints return the FULL updated user, so
  // that answer is held here until the prop has caught up.
  const [savedPhaseUser, setSavedPhaseUser] = useState(null);
  const propUser = useMemo(() => flatUsers.find((u) => u.id === selectedId) || null, [flatUsers, selectedId]);
  const selectedUser = useMemo(
    () => (savedPhaseUser && propUser && savedPhaseUser.id === propUser.id
      ? { ...propUser, phases: savedPhaseUser.phases || [] }
      : propUser),
    [propUser, savedPhaseUser]
  );
  useEffect(() => {
    if (!savedPhaseUser) return;
    // id + version identify a phase revision — once the prop carries the same set
    // the local answer is redundant and must go, or it would mask later changes.
    // Checked against the whole list, not the selection, so switching people and
    // back does not resurrect the stale numbers.
    const key = (u) => JSON.stringify((u?.phases || []).map((p) => [p.id, p.version]));
    const fromProp = (users || []).find((u) => u.id === savedPhaseUser.id);
    if (!fromProp || key(fromProp) === key(savedPhaseUser)) setSavedPhaseUser(null);
  }, [users, savedPhaseUser]);
  // The detail panel is only shown once THIS person's entries are actually loaded —
  // otherwise the tiles would briefly render with the previous person's numbers.
  // Deliberately WITHOUT `!loadingEntries`: every workforce broadcast reloads the
  // entries, and gating on the loading flag unmounted the whole detail view (and
  // any open Urlaub/Krank editor) each time someone somewhere stopped a timer.
  // While a same-person reload runs, the old data simply stays on screen.
  const dataReady = entriesUserId === selectedId;

  // ---- Employment phase scope -------------------------------------------------
  // The whole detail view is scoped to ONE phase: a Praktikum is then evaluated on
  // its own terms instead of being mixed into a later Vollzeit.
  const phases = useMemo(
    () => [...(selectedUser?.phases || [])].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date))),
    [selectedUser]
  );
  const activePhase = useMemo(() => {
    if (phases.length === 0) return null;
    // Same default as the Zeit tab (activePhaseOf) so both views agree on which
    // phase a person is judged by when nothing is picked explicitly. todayIso
    // keeps the default fresh across midnight (phase-change day).
    return phases.find((p) => p.id === selectedPhaseId) || activePhaseOf({ phases }, parseISODateLocal(todayIso));
  }, [phases, selectedPhaseId, todayIso]);
  const activePhaseId = activePhase?.id ?? null;
  useEffect(() => { setSelectedPhaseId(null); }, [selectedId]);

  // The person AS THEY WERE during this phase. Time.js is already phase-aware, so
  // narrowing `phases` to the selected one makes the Soll zero outside it. The role
  // mapping is what matters visually: only a real Werkstudenten-phase keeps the
  // Werkstudenten-Soll UI — Praktikum/Vollzeit show plain tracked hours and +/−.
  const phaseUser = useMemo(
    () => scopeUserToPhase(selectedUser, activePhase) || selectedUser,
    [selectedUser, activePhase]
  );

  const phaseEntries = useMemo(
    () => scopeEntriesToPhase(userEntries.filter((e) => e.user_id === selectedId), activePhase),
    [userEntries, selectedId, activePhase]
  );

  // Every request that TOUCHES the phase window (ranges may straddle it) — this is
  // the list the panel shows and edits, so it keeps the ORIGINAL dates.
  const phaseVacations = useMemo(() => {
    const mine = allVacations.filter((v) => v.user_id === selectedId);
    if (!activePhase) return mine;
    return mine.filter((v) => v.end_date >= activePhase.start_date
      && (!activePhase.end_date || v.start_date < activePhase.end_date));
  }, [allVacations, selectedId, activePhase]);

  // Same requests, clipped to the phase window — everything that COUNTS (credit
  // hours, used vacation days) must use these so days outside the phase drop out.
  const phaseVacationsClipped = useMemo(
    () => clipVacationsToPhase(phaseVacations, activePhase),
    [phaseVacations, activePhase]
  );

  // Shared year navigation so Urlaub/Krank/Stundenkonto all show the SAME year and
  // the same bounds — switching tabs never makes the year jump.
  const nowYear = Number(todayIso.slice(0, 4));
  const [yearView, setYearView] = useState(nowYear);
  useEffect(() => { setYearView(nowYear); }, [selectedId, activePhaseId, nowYear]);
  const yearBounds = useMemo(() => {
    const u = phaseUser;
    if (!u) return { min: nowYear, max: nowYear + 1 };
    let min = u.employed_since ? Number(u.employed_since.slice(0, 4)) : nowYear;
    // With a phase selected its start IS the lower bound — only widen from the data
    // in the no-phase fallback (a straddling vacation must not open earlier years).
    if (!activePhase) {
      for (const e of phaseEntries) { const y = new Date(e.started_at).getFullYear(); if (y < min) min = y; }
      for (const v of phaseVacations) { const y = Number(v.start_date.slice(0, 4)); if (y < min) min = y; }
    }
    // Departed employees / finished phases: never past their exit year. Otherwise plan one year ahead.
    const max = u.employment_end ? Number(u.employment_end.slice(0, 4)) : nowYear + 1;
    // Planning never goes further back than 2024.
    return { min: Math.max(2024, min), max: Math.max(Math.max(2024, min), max) };
  }, [phaseUser, activePhase, phaseEntries, phaseVacations, nowYear]);
  useEffect(() => {
    setYearView((y) => Math.min(Math.max(y, yearBounds.min), yearBounds.max));
  }, [yearBounds]);

  // ---- Stats for the three tab tiles ----
  const tileStats = useMemo(() => {
    if (!phaseUser) return null;
    const today = parseISODateLocal(todayIso);
    // A finished phase has no data in the current year — reference its last year
    // instead (and that year's end as "now"), otherwise every tile would read 0.
    const year = Math.min(Math.max(nowYear, yearBounds.min), yearBounds.max);
    const refDay = year === nowYear ? today : new Date(year, 11, 31);

    // Urlaubskonto: open days for the reference year via the canonical, phase-scoped
    // account (entitlement + carry-over from the previous year − used). It also hands
    // back the synthetic holiday/vacation credit entries the Stundenkonto tile needs.
    const acct = vacationAccountForPhase(selectedUser, activePhase, phaseEntries, phaseVacations, holidays, year, refDay);
    const holidayEntries = acct.holidayEntries;
    const vacationEntries = acct.vacationEntries;
    const vacBal = acct.balance;
    // "Offen" = nur der Jahresanspruch; der Übertrag hat seine eigene Frist
    // (31.03.) und wird separat ausgewiesen statt in die Zahl gemischt.
    const vacOpen = vacBal.annualRemaining;
    const overallDiffMs = accountBalanceMs(phaseUser, phaseEntries, holidayEntries, vacationEntries, today);
    const fmtSig = (ms) => {
      const totalMin = Math.round(Math.abs(ms) / 60000);
      const sign = totalMin === 0 ? "±" : ms < 0 ? "−" : "+";
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `${sign}${h}:${String(m).padStart(2, "0")}`;
    };

    // Kranktage all-time — count per day (half day = 0.5) and the year span for the
    // average shown on the tile.
    const sickDates = new Set();
    let sickTotal = 0;
    let firstSickYear = null;
    for (const e of phaseEntries) {
      if (e.kind !== "sick") continue;
      const iso = formatISODate(new Date(e.started_at));
      if (sickDates.has(iso)) continue;
      sickDates.add(iso);
      sickTotal += isHalfSickEntry(phaseUser, e) ? 0.5 : 1;
      const y = Number(iso.slice(0, 4));
      if (firstSickYear === null || y < firstSickYear) firstSickYear = y;
    }
    let firstYear = firstSickYear ?? year;
    if (phaseUser.employed_since) firstYear = Math.min(firstYear, Number(phaseUser.employed_since.slice(0, 4)));
    const sickAvg = sickTotal / Math.max(1, year - firstYear + 1);

    // Project vs. internal split over the whole PHASE (all tracked "work"):
    // project = time booked on any project (capped at worked), internal = the rest.
    let workMs = 0, projMs = 0;
    for (const e of phaseEntries) {
      if ((e.kind || "work") === "work" && e.ended_at) {
        workMs += Math.max(0, new Date(e.ended_at) - new Date(e.started_at));
      }
      if (Array.isArray(e.project_times)) {
        for (const pt of e.project_times) projMs += (pt.seconds || 0) * 1000;
      }
    }
    const projCapped = Math.min(projMs, workMs);
    const projPct = workMs > 0 ? Math.round((projCapped / workMs) * 1000) / 10 : 0;

    return {
      vacOpen,
      vacCarry: vacBal.carryRemaining,
      vacCarryExpires: vacBal.expiresOn,
      stundenSigned: fmtSig(overallDiffMs),
      stundenSign: Math.round(Math.abs(overallDiffMs) / 60000) === 0 ? "is-zero" : overallDiffMs > 0 ? "is-pos" : "is-neg",
      hasSplit: workMs > 0,
      projPct,
      internPct: workMs > 0 ? Math.round((100 - projPct) * 10) / 10 : 0,
      sickAvg,
    };
  }, [selectedUser, activePhase, phaseUser, phaseVacations, phaseEntries, holidays, nowYear, yearBounds, todayIso]);

  // Errors propagate to EmployeeForm.submit, which shows them INSIDE the modal —
  // the page banner lies underneath the overlay, and swallowing here also let
  // the edit modal close as if saving had succeeded.
  const handleSave = async (form) => {
    setErr("");
    if (creating) {
      const abbr = form.abbreviation || defaultAbbreviation(form.username);
      const created = await onCreateUser(form.username, form.password, form.role, form.color, abbr, form.email || null);
      // Everything else (Stunden, Urlaub, Zeiterfassung, Arbeitstage) comes from
      // the employment phases, not from the person form.
      if (created?.id != null) setSelectedId(created.id);
      setCreating(false);
    } else if (selectedUser) {
      const updates = {};
      if (form.username !== selectedUser.username) updates.username = form.username;
      if (form.role !== selectedUser.role) updates.role = form.role;
      if (form.color !== selectedUser.color) updates.color = form.color;
      if ((form.abbreviation || "") !== (selectedUser.abbreviation || "")) updates.abbreviation = form.abbreviation || "";
      if ((form.email || "") !== (selectedUser.email || "")) updates.email = form.email || null;
      if (form.password) updates.password = form.password;
      if (Object.keys(updates).length > 0) {
        await onUpdateUser(selectedUser.id, updates);
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await onDeleteUser(confirmDel.id);
      if (selectedId === confirmDel.id) setSelectedId(null);
    } catch (e) { setErr(e.message); }
    setConfirmDel(null);
  };

  const startCreate = () => {
    setCreating(true);
  };

  // Phase picker on the right of the header: switch phase, edit one (pencil per
  // row), add a new one. It is the ONLY entry point to phase management.
  const phasePicker = !selectedUser ? null : phases.length === 0 ? (
    <button type="button" className="btn btn-secondary emp-phase-new-btn" onClick={() => setPhaseEditor({ phase: null })}>
      + Phase
    </button>
  ) : (
    <PhasePicker
      phases={phases}
      activeId={activePhaseId}
      onSelect={(id) => setSelectedPhaseId(id)}
      onEdit={(p) => setPhaseEditor({ phase: p })}
      onCreate={() => setPhaseEditor({ phase: null })}
    />
  );

  if (!ready || !holidaysLoaded) {
    return (
      <div
        className="emp-admin-page"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text-muted)" }}
      >
        Laden…
      </div>
    );
  }

  return (
    <div className="emp-admin-page">
      {err && <div className="wf-form-error emp-error-bar">{err} <button onClick={() => setErr("")}>&times;</button></div>}

      <div className="emp-layout">
        <aside className="emp-sidebar">
          <button
            type="button"
            className="emp-side-item emp-side-add"
            onClick={startCreate}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span className="emp-side-name">Neue Person</span>
          </button>
          <div className="emp-side-divider" />
          {[
            { items: sortedUsers.admins, key: "admins" },
            { items: sortedUsers.active, key: "active" },
            { items: sortedUsers.archived, key: "archived" },
          ].map((group, gi, all) => {
            const showDivider = gi > 0 && group.items.length > 0 && all.slice(0, gi).some((g) => g.items.length > 0);
            return (
              <React.Fragment key={group.key}>
                {showDivider && <div className="emp-side-divider" />}
                {group.items.map((u) => {
                  const isSel = selectedId === u.id;
                  const pending = pendingVacationUserIds.has(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`emp-side-item ${isSel ? "is-active" : ""} ${u.archived ? "is-archived" : ""}`}
                      onClick={() => setSelectedId(u.id)}
                    >
                      <span className="emp-side-name-wrap">
                        <span className="emp-side-name">{u.username}</span>
                        {pending && <span className="emp-side-pending-badge" title="Offener Urlaubsantrag">!</span>}
                      </span>
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </aside>

        <main className="emp-main">
          {!selectedUser ? (
            <div className="emp-empty">Bitte eine Person auswählen.</div>
          ) : !dataReady ? (
            <>
              <div className="emp-main-header">
                <button
                  type="button"
                  className="emp-main-name emp-main-name-btn"
                  style={{ "--emp-name-color": selectedUser.color || "#6366f1" }}
                  onClick={() => setEditingPerson(true)}
                  title="Person bearbeiten"
                >
                  {selectedUser.username}
                </button>
                {phasePicker}
              </div>
              {entriesError ? (
                <div className="emp-loading-inline">
                  Zeiteinträge konnten nicht geladen werden.{" "}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={loadEntries}>Erneut versuchen</button>
                </div>
              ) : (
                <div className="emp-loading-inline">Laden…</div>
              )}
            </>
          ) : (
            <>
              <div className="emp-main-header">
                <button
                  type="button"
                  className="emp-main-name emp-main-name-btn"
                  style={{ "--emp-name-color": selectedUser.color || "#6366f1" }}
                  onClick={() => setEditingPerson(true)}
                  title="Person bearbeiten"
                >
                  {selectedUser.username}
                </button>
                {phasePicker}
              </div>

              <div className="emp-tab-tiles">
                <button
                  type="button"
                  className={`emp-tab-tile emp-tab-tile-stunden ${tab === "stunden" ? "is-active" : ""}`}
                  onClick={() => setTab("stunden")}
                >
                  <span className="emp-tab-tile-main">
                    <span className="emp-tab-tile-label">Stundenkonto</span>
                    <span className={`emp-tab-tile-value ${tileStats?.stundenSign || ""}`}>
                      {tileStats ? (
                        <>
                          <span className="emp-tab-tile-sep">{tileStats.stundenSigned.charAt(0)}</span>
                          {tileStats.stundenSigned.slice(1)}
                        </>
                      ) : "—"}
                      <span className="emp-tab-tile-unit">h</span>
                    </span>
                  </span>
                  {tileStats?.hasSplit && (
                    <span className="emp-tab-tile-split" title="Anteil Projekte / intern über die gesamte Phase">
                      <span className="emp-tab-split-row">
                        <span className="emp-tab-split-label">Projekte</span>
                        <span className="emp-tab-split-pct">{tileStats.projPct.toFixed(1)}%</span>
                      </span>
                      <span className="emp-tab-split-row">
                        <span className="emp-tab-split-label">Intern</span>
                        <span className="emp-tab-split-pct">{tileStats.internPct.toFixed(1)}%</span>
                      </span>
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`emp-tab-tile ${tab === "vacation" ? "is-active" : ""}`}
                  onClick={() => setTab("vacation")}
                >
                  <span className="emp-tab-tile-label">Urlaubskonto</span>
                  <span className="emp-tab-tile-value is-vacation">
                    {tileStats ? fmtNumDot(tileStats.vacOpen) : "—"}
                    {tileStats && tileStats.vacCarry > 0 && (
                      <span
                        className="emp-tab-tile-carry"
                        title={`Zusätzlich ${fmtNumDot(tileStats.vacCarry)} Tage Übertrag — verfällt ${fmtDateDE(tileStats.vacCarryExpires)}`}
                      >+{fmtNumDot(tileStats.vacCarry)}</span>
                    )}
                    <span className="emp-tab-tile-unit">Offen</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`emp-tab-tile ${tab === "sick" ? "is-active" : ""}`}
                  onClick={() => setTab("sick")}
                >
                  <span className="emp-tab-tile-label">Kranktage</span>
                  <span className="emp-tab-tile-value is-sick">
                    <span className="emp-tab-tile-avg">Ø</span>
                    {tileStats ? tileStats.sickAvg.toFixed(2) : "—"}
                    <span className="emp-tab-tile-unit">/ Jahr</span>
                  </span>
                </button>
              </div>

              <div className="emp-tab-content">
                {tab === "vacation" && (
                  <VacationPanel
                    user={phaseUser}
                    phase={activePhase}
                    vacations={phaseVacations}
                    entries={phaseEntries}
                    holidays={holidays}
                    viewYear={yearView}
                    setViewYear={setYearView}
                    minYear={yearBounds.min}
                    maxYear={yearBounds.max}
                    onChanged={loadVacations}
                    setErr={setErr}
                  />
                )}
                {tab === "sick" && (
                  <SickPanel
                    user={phaseUser}
                    entries={phaseEntries.filter((e) => e.kind === "sick")}
                    holidays={holidays}
                    viewYear={yearView}
                    setViewYear={setYearView}
                    minYear={yearBounds.min}
                    maxYear={yearBounds.max}
                    onChanged={loadEntries}
                    setErr={setErr}
                  />
                )}
                {tab === "stunden" && (
                  <HoursAccountPanel
                    user={phaseUser}
                    entries={phaseEntries}
                    vacations={phaseVacationsClipped}
                    holidays={holidays}
                    semesterBreaks={semesterBreaks}
                    viewYear={yearView}
                    setViewYear={setYearView}
                    minYear={yearBounds.min}
                    maxYear={yearBounds.max}
                    onSaveUser={onUpdateUser}
                    projects={projects}
                  />
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {creating && (
        <div className="modal-overlay" onClick={() => setCreating(false)}>
          <div className="modal-content emp-person-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Neue Person</h2>
              <button className="modal-close-btn" onClick={() => setCreating(false)}>&times;</button>
            </div>
            <EmployeeForm
              key="new"
              user={null}
              onSave={async (form) => { await handleSave(form); }}
              onDelete={null}
              onCancelCreate={() => setCreating(false)}
            />
          </div>
        </div>
      )}

      {editingPerson && selectedUser && (
        <div className="modal-overlay" onClick={() => setEditingPerson(false)}>
          <div className="modal-content emp-person-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Person bearbeiten</h2>
              <button className="modal-close-btn" onClick={() => setEditingPerson(false)}>&times;</button>
            </div>
            <EmployeeForm
              key={`edit-${selectedUser.id}`}
              user={selectedUser}
              onSave={async (form) => { await handleSave(form); setEditingPerson(false); }}
              onDelete={() => { setEditingPerson(false); setConfirmDel(selectedUser); }}
              onCancelCreate={null}
              onArchiveToggle={async () => {
                if (selectedUser.archived) {
                  // Re-activate: clear the leave date again.
                  try { await onUpdateUser(selectedUser.id, { archived: false, employment_end: "" }); setEditingPerson(false); }
                  catch (e) { setErr(e.message); }
                } else {
                  // Archiving asks for the leave date first.
                  setEditingPerson(false);
                  setArchiveTarget(selectedUser);
                }
              }}
            />
          </div>
        </div>
      )}

      {phaseEditor && selectedUser && (
        <PhaseEditorModal
          key={phaseEditor.phase?.id ?? "new"}
          userId={selectedUser.id}
          phase={phaseEditor.phase}
          isAdminPerson={selectedUser.role === "admin"}
          defaultStart={selectedUser.employed_since || ""}
          setErr={setErr}
          onClose={() => setPhaseEditor(null)}
          onSaved={(phaseId, updatedUser) => {
            setPhaseEditor(null);
            if (updatedUser) setSavedPhaseUser(updatedUser);
            if (phaseId != null) setSelectedPhaseId(phaseId);
          }}
          onDeleted={(updatedUser) => {
            setPhaseEditor(null);
            if (updatedUser) setSavedPhaseUser(updatedUser);
            setSelectedPhaseId(null);
          }}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          message={`Person „${confirmDel.username}“ wirklich löschen?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {archiveTarget && (
        <ArchiveDialog
          user={archiveTarget}
          onConfirm={async (endDate) => {
            try {
              await onUpdateUser(archiveTarget.id, { archived: true, employment_end: endDate || "" });
              // The leave date belongs to the employment phase — close the still-open
              // one so its Soll stops on that day (stored end is exclusive).
              const open = (archiveTarget.phases || []).find((p) => !p.end_date);
              if (open && endDate) {
                const updated = await updatePhase(open.id, {
                  start_date: open.start_date,
                  end_date: isoShift(endDate, 1),
                  type: open.type,
                  weekly_hours: open.weekly_hours,
                  vacation_rule: open.vacation_rule,
                  vacation_days: open.vacation_days,
                  tracks_time: open.tracks_time,
                  fixed_workdays: open.fixed_workdays || "",
                  version: open.version,
                });
                setSavedPhaseUser(updated);
              }
              setArchiveTarget(null);
            }
            catch (e) { setErr(e.message); setArchiveTarget(null); }
          }}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
    </div>
  );
}

function ArchiveDialog({ user, onConfirm, onCancel }) {
  const [endDate, setEndDate] = useState(user?.employment_end || formatISODate(new Date()));
  const [saving, setSaving] = useState(false);
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content emp-archive-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Person archivieren</h2>
          <button className="modal-close-btn" onClick={onCancel}>&times;</button>
        </div>
        <p className="emp-archive-text">
          „{user.username}“ wird archiviert. Ab welchem Tag ist die Person ausgetreten?
        </p>
        <div className="emp-form-field">
          <label className="emp-form-label">Austrittsdatum</label>
          <DatePicker value={endDate} onChange={setEndDate} clearable shortYear />
        </div>
        <div className="wf-form-actions wf-form-actions-right">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={async () => { setSaving(true); await onConfirm(endDate); }}
          >
            Archivieren
          </button>
        </div>
      </div>
    </div>
  );
}

const PHASE_TYPE_OPTS = [
  { value: "praktikum", label: "Praktikum" },
  { value: "working_student", label: "Werkstudent" },
  { value: "full_time", label: "Vollzeit" },
  { value: "minijob", label: "Minijob" },
];
const VAC_RULE_OPTS = [
  { value: "flat", label: "Fix (Jahr)" },
  { value: "phase_total", label: "Fix (Phase)" },
  { value: "prorated", label: "Anteilig (Stunden)" },
  { value: "none", label: "Kein Urlaub" },
];
// The number field next to the rule means something different per rule — the label
// spells out which unit the entered days are in.
const VAC_DAYS_LABEL = {
  flat: "Urlaubstage (Jahr)",
  phase_total: "Urlaubstage (Phase)",
};
const PHASE_TYPE_LABEL = Object.fromEntries(PHASE_TYPE_OPTS.map((o) => [o.value, o.label]));

// Phases store half-open [start, end) with end EXCLUSIVE, but the user thinks in
// inclusive "bis"-dates — so shift by ±1 day at the UI boundary. Empty-tolerant
// wrapper around the shared lib helper.
function isoShift(iso, days) {
  return iso ? isoAddDays(iso, days) : "";
}
function fmtDatePhase(iso) {
  if (!iso) return "offen";
  const [y, m, d] = String(iso).split("-");
  return `${d}.${m}.${y}`;
}
// Dropdown label — just the kind ("Werkstudent", "Vollzeit", …); the dates live
// in the phase editor.
function phaseLabel(p) {
  return PHASE_TYPE_LABEL[p.type] || p.type;
}

// Phase dropdown with a per-row edit button — CustomSelect can't do that, so this
// is a slim portal dropdown reusing the custom-select styling.
function PhasePicker({ phases, activeId, onSelect, onEdit, onCreate }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    // Anchored on the RIGHT edge so the list stays flush with the header's right side.
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right, minWidth: rect.width });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Fixed-position portal — page scrolling would detach it from the trigger;
  // close instead (scrolling inside the list stays allowed).
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const active = phases.find((p) => p.id === activeId);

  return (
    <div className="emp-phase-picker">
      <button
        ref={triggerRef}
        type="button"
        className={`custom-select-trigger emp-phase-trigger ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{active ? phaseLabel(active) : "Phase"}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="custom-select-dropdown emp-phase-dropdown"
          style={{ position: "fixed", top: pos.top, right: pos.right, minWidth: pos.minWidth }}
        >
          {phases.map((p) => (
            <div key={p.id} className={`emp-phase-option ${p.id === activeId ? "is-active" : ""}`}>
              <button
                type="button"
                className="emp-phase-option-main"
                onClick={() => { setOpen(false); onSelect(p.id); }}
              >
                {phaseLabel(p)}
              </button>
              <button
                type="button"
                className="emp-phase-option-edit"
                title="Phase bearbeiten"
                onClick={() => { setOpen(false); onEdit(p); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
              </button>
            </div>
          ))}
          <button
            type="button"
            className="custom-select-option emp-phase-option-new"
            onClick={() => { setOpen(false); onCreate(); }}
          >
            + Neue Phase
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

// Create / edit / delete ONE employment phase — reached from the phase dropdown
// next to the person's name. Dates are shown and entered INCLUSIVE and converted
// to the stored half-open interval on save.
function PhaseEditorModal({ userId, phase, isAdminPerson, defaultStart, onClose, onSaved, onDeleted, setErr }) {
  const [form, setForm] = useState(() => (phase
    ? {
      id: phase.id, version: phase.version, start_date: phase.start_date,
      end_date: isoShift(phase.end_date, -1), type: phase.type, weekly_hours: phase.weekly_hours,
      vacation_rule: phase.vacation_rule, vacation_days: phase.vacation_days,
      tracks_time: phase.tracks_time, fixed_workdays: phase.fixed_workdays || "",
    }
    : {
      start_date: defaultStart || "", end_date: "", type: "full_time", weekly_hours: 40,
      vacation_rule: "flat", vacation_days: 30, tracks_time: true, fixed_workdays: "",
    }));
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const isWorkingStudent = form.type === "working_student";

  // Werkstudent standard is 20 h/week, everything else 40 — only overwrite when the
  // current value is still the other default (a custom value stays untouched).
  const changeType = (v) => setForm((s) => {
    const h = Number(s.weekly_hours);
    let weekly_hours = s.weekly_hours;
    if (v === "working_student" && (!h || h === 40)) weekly_hours = 20;
    else if (v !== "working_student" && (!h || h === 20)) weekly_hours = 40;
    return { ...s, type: v, weekly_hours };
  });

  const save = async () => {
    if (!form.start_date) { setErr && setErr("Startdatum fehlt."); return; }
    // Without an end date the whole allowance would land in the start year and the
    // person would sit at zero from 01.04. of the next year onwards.
    if (form.vacation_rule === "phase_total" && !form.end_date) {
      setErr && setErr("Bei „Fix (Phase)“ wird ein Enddatum gebraucht — sonst lässt sich der Anspruch nicht verteilen.");
      return;
    }
    setSaving(true);
    const payload = {
      start_date: form.start_date,
      end_date: form.end_date ? isoShift(form.end_date, 1) : null,  // inclusive → exclusive
      type: form.type,
      weekly_hours: Number(form.weekly_hours) || 0,
      vacation_rule: form.vacation_rule,
      vacation_days: Number(form.vacation_days) || 0,
      tracks_time: !!form.tracks_time,
      fixed_workdays: form.fixed_workdays || "",
    };
    try {
      // Both endpoints return the whole user — handed up so the view can use the
      // fresh phases immediately instead of waiting for the users list to reload.
      if (form.id) {
        const updated = await updatePhase(form.id, { ...payload, version: form.version });
        onSaved(form.id, updated);
      } else {
        // Pick the fresh phase by its start date so the dropdown can jump straight
        // to what was just created.
        const updated = await createPhase(userId, payload);
        const created = (updated?.phases || []).find((p) => p.start_date === payload.start_date);
        onSaved(created?.id ?? null, updated);
      }
    } catch (e) { setErr && setErr(e.message); setSaving(false); }
  };

  const remove = async () => {
    try { const updated = await deletePhase(form.id); onDeleted(updated); }
    catch (e) { setErr && setErr(e.message); setConfirmDel(false); }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content emp-phase-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">{form.id ? "Phase bearbeiten" : "Neue Phase"}</h2>
            <button className="modal-close-btn" onClick={onClose}>&times;</button>
          </div>
          <div className="emp-phase-form">
            <label className="wf-field"><span>Art</span>
              <CustomSelect value={form.type} options={PHASE_TYPE_OPTS} onChange={changeType} /></label>
            <div className="wf-field-row">
              <label className="wf-field"><span>Von</span>
                <DatePicker value={form.start_date} onChange={(d) => setForm((s) => ({ ...s, start_date: d }))} clearable={false} /></label>
              <label className="wf-field"><span>Bis (leer = laufend)</span>
                <DatePicker value={form.end_date} onChange={(d) => setForm((s) => ({ ...s, end_date: d }))} clearable /></label>
            </div>
            <div className="wf-field-row">
              <label className="wf-field"><span>Urlaubsregel</span>
                <CustomSelect value={form.vacation_rule} options={VAC_RULE_OPTS} onChange={(v) => setForm((s) => ({ ...s, vacation_rule: v }))} /></label>
              <label className="wf-field"><span>{VAC_DAYS_LABEL[form.vacation_rule] || "Urlaubstage (Basis)"}</span>
                <input className="wf-input wf-input-no-spin" type="number" min="0" step="0.5" value={form.vacation_days} disabled={form.vacation_rule === "none"} onChange={(e) => setForm((s) => ({ ...s, vacation_days: e.target.value }))} /></label>
            </div>
            {isWorkingStudent && (
              <div className="wf-field">
                <span>Feste Arbeitstage <span className="emp-field-hint">(leer = flexibel)</span></span>
                <WorkdayChips value={form.fixed_workdays} onChange={(v) => setForm((s) => ({ ...s, fixed_workdays: v }))} />
              </div>
            )}
            {/* Werkstudenten have no Stunden/Woche field — the row then holds the
                toggle alone, and it collapses entirely for a non-admin. */}
            {(!isWorkingStudent || isAdminPerson) && (
              <div className="wf-field-row emp-phase-hours-row">
                {!isWorkingStudent && (
                  <label className="wf-field"><span>Stunden/Woche</span>
                    <input className="wf-input wf-input-no-spin" type="number" min="0" step="0.5" value={form.weekly_hours} onChange={(e) => setForm((s) => ({ ...s, weekly_hours: e.target.value }))} /></label>
                )}
                {isAdminPerson && (
                  // Shown INVERTED: ticked = the hours are credited automatically,
                  // which is exactly `tracks_time === false` in the DB. Only the
                  // checkbox flips it — the stored field keeps its meaning.
                  <label className="emp-halfday-toggle emp-phase-toggle">
                    <input type="checkbox" checked={!form.tracks_time} onChange={(e) => setForm((s) => ({ ...s, tracks_time: !e.target.checked }))} />
                    <span>Automatische Zeiterfassung</span>
                  </label>
                )}
              </div>
            )}
            <div className="wf-form-actions">
              {form.id && <button type="button" className="btn btn-danger" onClick={() => setConfirmDel(true)} disabled={saving}>Löschen</button>}
              <div className="wf-form-actions-right">
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmDel && (
        <ConfirmDialog
          message={`Phase ${fmtDatePhase(form.start_date)} – ${fmtDatePhase(form.end_date)} löschen?`}
          onConfirm={remove}
          onCancel={() => setConfirmDel(false)}
        />
      )}
    </>
  );
}

function EmployeeForm({ user, onSave, onDelete, onArchiveToggle }) {
  const isNew = !user?.id;
  const [form, setForm] = useState({
    username: user?.username || "",
    abbreviation: user?.abbreviation || "",
    role: user?.role || "employee",
    color: user?.color || "#6366f1",
    email: user?.email || "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState(null);
  const colorBtnRef = useRef(null);
  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (!showColorPicker) return;
    const onDoc = (e) => {
      if (colorBtnRef.current && !colorBtnRef.current.contains(e.target) &&
          colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showColorPicker]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.username.trim()) { setErr("Bitte einen Namen angeben."); return; }
    if (isNew && (!form.password || form.password.length < 6)) {
      setErr("Bitte ein Passwort mit mindestens 6 Zeichen angeben.");
      return;
    }
    try {
      await onSave({
        ...form,
        username: form.username.trim(),
        abbreviation: form.abbreviation.trim(),
        email: form.email.trim(),
      });
    } catch (e) {
      setErr(e.message || "Fehler beim Speichern");
    }
  };

  return (
    <form className="emp-form" onSubmit={submit}>
      <div className="emp-form-row emp-form-row-identity">
        <div className="emp-form-field emp-form-field-color">
          <label className="emp-form-label">Farbe</label>
          <button
            ref={colorBtnRef}
            type="button"
            className="emp-color-btn"
            style={{ background: form.color }}
            onClick={() => {
              if (!showColorPicker && colorBtnRef.current) {
                const rect = colorBtnRef.current.getBoundingClientRect();
                // Desktop keeps the popover right next to the swatch. On a phone
                // that would push it off-screen, so clamp it into the viewport
                // (the popover is ~226 x 250 px).
                if (window.innerWidth <= 768) {
                  const W = 226, H = 250, PAD = 8;
                  const left = Math.max(PAD, Math.min(rect.right + 8, window.innerWidth - W - PAD));
                  const top = Math.max(PAD, Math.min(rect.top, window.innerHeight - H - PAD));
                  setColorPickerPos({ top, left });
                } else {
                  setColorPickerPos({ top: rect.top, left: rect.right + 8 });
                }
              }
              setShowColorPicker(!showColorPicker);
            }}
            title="Farbe wählen"
          />
        </div>
        <div className="emp-form-field emp-form-field-abbr">
          <label className="emp-form-label">Kürzel</label>
          <input className="wf-input" maxLength={5} value={form.abbreviation}
            onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase().slice(0, 5) })} />
        </div>
        <div className="emp-form-field emp-form-field-name">
          <label className="emp-form-label">Name</label>
          <input className="wf-input" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </div>
      </div>

      <div className="emp-form-row">
        <div className="emp-form-field">
          <label className="emp-form-label">Rolle</label>
          <CustomSelect
            value={form.role}
            /* Nur Berechtigung — ob jemand Werkstudent:in ist, steht in der
               Beschäftigungsphase (Typ), nicht in der Rolle. */
            options={[
              { value: "employee", label: "Mitarbeiter" },
              { value: "admin", label: "Admin" },
            ]}
            onChange={(v) => setForm((f) => ({ ...f, role: v }))}
          />
        </div>
      </div>

      <div className="emp-form-field emp-form-field-email">
        <label className="emp-form-label">E-Mail</label>
        <input type="email" className="wf-input" value={form.email}
          placeholder="optional"
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>

      <div className="emp-form-field emp-form-field-password">
        <label className="emp-form-label">{isNew ? "Passwort" : "Passwort ändern"}</label>
        <input type="password" className="wf-input" value={form.password}
          placeholder={isNew ? "min. 6 Zeichen" : "leer lassen, um nichts zu ändern"}
          onChange={(e) => setForm({ ...form, password: e.target.value })} />
      </div>

      {err && <div className="wf-form-error">{err}</div>}

      <div className="wf-form-actions emp-form-actions-centered">
        <div className="wf-form-actions-left">
          {!isNew && onDelete && (
            <button type="button" className="btn btn-danger" onClick={onDelete}>Löschen</button>
          )}
        </div>
        {!isNew && onArchiveToggle && (
          <button type="button" className="btn btn-secondary emp-form-archive-btn" onClick={onArchiveToggle}>
            {user?.archived ? "Reaktivieren" : "Archivieren"}
          </button>
        )}
        <div className="wf-form-actions-right">
          <button type="submit" className="btn btn-primary">{isNew ? "Anlegen" : "Speichern"}</button>
        </div>
      </div>

      {showColorPicker && (
        <div className="area-color-popover" ref={colorPickerRef} style={colorPickerPos ? { top: colorPickerPos.top, left: colorPickerPos.left } : {}}>
          <HexColorPicker color={form.color} onChange={(c) => setForm({ ...form, color: c })} />
          <input
            className="area-color-hex-input"
            value={form.color}
            onChange={(e) => { const v = e.target.value; if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setForm({ ...form, color: v }); }}
            maxLength={7}
          />
        </div>
      )}
    </form>
  );
}

function VacationPanel({ user, phase, vacations, entries, holidays, viewYear, setViewYear, minYear, maxYear, onChanged, setErr }) {
  const todayISO = useToday();
  const today = useMemo(() => parseISODateLocal(todayISO), [todayISO]);
  const currentYear = today.getFullYear();

  const setStatus = async (v, status) => {
    try { await updateVacation(v.id, { status, version: v.version }); onChanged(); }
    catch (e) { setErr(e.message); onChanged(); }
  };

  // Admin editor: create/adjust a vacation directly for this employee.
  const [editor, setEditor] = useState(null); // {id?, version?, start, end, status}
  const [saving, setSaving] = useState(false);
  const [confirmDelVac, setConfirmDelVac] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null); // { v, status } | null
  // Range picking in the inline calendar: false = next click starts a new range.
  const [pickEnd, setPickEnd] = useState(false);
  const pickRangeDay = (y, m, d) => {
    const iso = `${y}-${pad2(m + 1)}-${pad2(d)}`;
    setEditor((s) => {
      if (!s) return s;
      if (!pickEnd) return { ...s, start: iso, end: iso };
      if (iso < s.start) return { ...s, start: iso };
      return { ...s, end: iso };
    });
    setPickEnd((p) => !p);
  };
  const openCreate = () => {
    setPickEnd(false);
    const iso = viewYear === currentYear ? todayISO : `${viewYear}-01-01`;
    setEditor({ start: iso, end: iso, status: "approved", half: false });
  };
  const openEdit = (v) => { setPickEnd(false); setEditor({ id: v.id, version: v.version, start: v.start_date, end: v.end_date, status: v.status, half: !!v.half_day || !!v.half_end }); };
  const closeEditor = () => { if (!saving) setEditor(null); };
  const saveEditor = async () => {
    if (!editor) return;
    if (editor.end < editor.start) { setErr && setErr("Ende darf nicht vor dem Start liegen."); return; }
    // Single day → half_day; range → half_end (last day is a half day).
    const halfDay = !!editor.half && editor.start === editor.end;
    const halfEnd = !!editor.half && editor.start !== editor.end;
    setSaving(true);
    try {
      if (editor.id) {
        await updateVacation(editor.id, { start_date: editor.start, end_date: editor.end, half_day: halfDay, half_end: halfEnd, status: editor.status, version: editor.version });
      } else {
        await createVacation(editor.start, editor.end, { user_id: user.id, status: editor.status, half_day: halfDay, half_end: halfEnd });
      }
      setEditor(null);
      onChanged();
    } catch (e) { setErr && setErr(e?.message || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };
  const deleteFromEditor = async () => {
    if (!editor?.id) return;
    setSaving(true);
    try { await deleteVacation(editor.id); setEditor(null); onChanged(); }
    catch (e) { setErr && setErr(e?.message || "Löschen fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const statusLabel = (v) => {
    if (v.status === "rejected" || v.status === "cancelled") return "storniert";
    if (v.status === "requested") return "beantragt";
    return "genehmigt";
  };

  // Filter to vacation requests that touch viewYear
  const yearStart = `${viewYear}-01-01`;
  const yearEnd = `${viewYear}-12-31`;
  const filteredVacations = vacations.filter((v) => v.start_date <= yearEnd && v.end_date >= yearStart);
  const sortedVacations = [...filteredVacations].sort((a, b) => a.start_date.localeCompare(b.start_date));

  // Year navigation is shared across all tabs (bounds come from the parent).
  const canPrev = viewYear > minYear;
  const canNext = viewYear < maxYear;

  // Open days for the viewed year via the canonical, phase-scoped account:
  // entitlement + carry-over from the previous year − used (carry-over first).
  // Memoised — computeCarryIn replays every year since the phase started and each
  // of those iterates all entries, far too expensive to redo on every render.
  const account = useMemo(
    () => vacationAccountForPhase(user, phase, entries, vacations, holidays, viewYear, today),
    [user, phase, entries, vacations, holidays, viewYear, today]
  );
  const carryForYear = account.carry;
  const balance = account.balance;
  const entitlement = balance.entitlement;
  // Nur der Jahresanspruch — der Übertrag steht als eigene Zahl daneben.
  const openThisYear = balance.annualRemaining;

  // The balance counts phase-CLIPPED records — the "Tage" column must count the
  // same days, otherwise a request straddling the phase boundary contradicts
  // "Offen". Rows still edit the ORIGINAL record (clipping is display-only).
  const clippedById = useMemo(() => {
    const m = new Map();
    for (const c of account.vacations) m.set(c.id, c);
    return m;
  }, [account.vacations]);

  return (
    <div className="emp-vacation">
      <div className="wf-vac-year-nav">
        <button type="button" className="wf-nav-btn" onClick={() => canPrev && setViewYear(viewYear - 1)} disabled={!canPrev} title="Vorheriges Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <span className="wf-vac-year-label">{viewYear}</span>
        <button type="button" className="wf-nav-btn" onClick={() => canNext && setViewYear(viewYear + 1)} disabled={!canNext} title="Nächstes Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        <button type="button" className="wf-nav-btn emp-sick-add emp-add-after-year" onClick={openCreate} title="Urlaub eintragen">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
        <div className="emp-nav-right">
          {balance.carryIn > 0 && (
            <span
              className="emp-vac-total emp-vac-carry-fig"
              title={balance.carryExpired
                ? `Übertrag ${viewYear - 1} verfallen`
                : `Übertrag aus ${viewYear - 1} · verfällt ${fmtDateDE(carryForYear.expires_on)}`}
            >
              {fmtNumDot(balance.carryRemaining)}<span className="emp-vac-total-sep">/</span>{fmtNumDot(balance.carryIn)}
              <span className="emp-sick-total-unit">Übertrag</span>
            </span>
          )}
          <span className="emp-vac-total" title="Offen / Jahresanspruch — ohne Übertrag (eigene Zahl davor)">
            {fmtNumDot(openThisYear)}<span className="emp-vac-total-sep">/</span>{fmtNumDot(entitlement)}
            <span className="emp-sick-total-unit">Offen</span>
          </span>
        </div>
      </div>

      <div className="emp-vac-list">
        <div className="wf-vac-row wf-vac-row-admin wf-vac-row-head">
          <span>Von</span>
          <span>Bis</span>
          <span>Tage</span>
          <span>Beantragt</span>
          <span className="wf-vac-status-col">Status</span>
          <span />
        </div>
        {sortedVacations.length === 0 && <div className="wf-empty-week">Kein Urlaub in {viewYear}.</div>}
        {sortedVacations.map((v) => {
          // Clip the displayed range to the currently viewed year so a cross-year
          // request shows up to 31.12. in year N and from 01.01. in year N+1.
          const yearStart = `${viewYear}-01-01`;
          const yearEnd = `${viewYear}-12-31`;
          // Count against the phase-CLIPPED record (like the balance) so the
          // column agrees with "Offen" even when the request straddles the
          // phase boundary; fully out-of-phase records count 0.
          const clipped = clippedById.get(v.id);
          const base = clipped ?? v;
          const dispStart = base.start_date < yearStart ? yearStart : base.start_date;
          const dispEnd = base.end_date > yearEnd ? yearEnd : base.end_date;
          // A phase-clipped cross-year request can end before the viewed year even
          // starts — the "clipped" range then inverts (Von 01.01. / Bis 31.12. of
          // the PREVIOUS year). Nothing of it lies in this year: skip the row.
          if (dispEnd < dispStart) return null;
          const days = clipped ? countedVacDays(dispStart, dispEnd, holidays, clipped) : 0;
          return (
            <div key={v.id} className="wf-vac-row wf-vac-row-admin wf-vac-row-clickable" onClick={() => openEdit(v)} title="Urlaub bearbeiten">
              <span className="wf-vac-cell wf-vac-cell-muted">{fmtDateDE(dispStart)}</span>
              <span className="wf-vac-cell wf-vac-cell-muted">{dispStart === dispEnd ? "" : fmtDateDE(dispEnd)}</span>
              <span className="wf-vac-cell wf-vac-cell-muted">{fmtDays(days)}</span>
              {/* created_at is a UTC timestamp — convert to the LOCAL day first,
                  or a request placed between 00:00 and 02:00 shows yesterday. */}
              <span className="wf-vac-cell wf-vac-cell-faint">{fmtDateDE(formatISODate(new Date(v.created_at)))}</span>
              <span className={`wf-vac-status is-${v.status}`}>{statusLabel(v)}</span>
              <span className="wf-vac-acts">
                <button className="wf-vac-act wf-vac-approve" title="Genehmigen" disabled={v.status === "approved"} onClick={(e) => { e.stopPropagation(); if (v.status === "rejected") setConfirmStatus({ v, status: "approved" }); else setStatus(v, "approved"); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </button>
                <button className="wf-vac-act wf-vac-reject" title="Stornieren" disabled={v.status === "rejected"} onClick={(e) => { e.stopPropagation(); setConfirmStatus({ v, status: "rejected" }); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {editor && (
        <div className="modal-overlay" onMouseDown={closeEditor}>
          <div className="modal-content emp-entry-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editor.id ? "Urlaub bearbeiten" : "Urlaub eintragen"}</h2>
              <button className="modal-close-btn" onClick={closeEditor} disabled={saving}>&times;</button>
            </div>
            <div className="emp-range-cal emp-range-cal-solo">
              <MiniCalendar
                currentYear={Number(editor.start.slice(0, 4))}
                currentMonth={Number(editor.start.slice(5, 7)) - 1}
                onSelect={pickRangeDay}
                rangeStart={editor.start}
                rangeEnd={editor.end}
                hideToday
              />
            </div>
            <div className="wf-form-actions emp-sick-actions">
              <div className="wf-form-actions-left">
                {editor.id && (
                  <button type="button" className="btn btn-danger" onClick={() => setConfirmDelVac(true)} disabled={saving}>Löschen</button>
                )}
              </div>
              <label className="emp-halfday-toggle emp-halfday-center">
                <input
                  type="checkbox"
                  checked={!!editor.half}
                  onChange={(e) => setEditor((s) => ({ ...s, half: e.target.checked }))}
                />
                <span>{editor.start === editor.end ? "Halber Tag" : "Letzter Tag halb"}</span>
              </label>
              <div className="wf-form-actions-right">
                <button type="button" className="btn btn-primary" onClick={saveEditor} disabled={saving}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelVac && editor && (
        <ConfirmDialog
          message={`Urlaub von ${fmtDateDE(editor.start)} bis ${fmtDateDE(editor.end)} löschen?`}
          onConfirm={async () => { setConfirmDelVac(false); await deleteFromEditor(); }}
          onCancel={() => setConfirmDelVac(false)}
        />
      )}

      {confirmStatus && (
        <ConfirmDialog
          message={`Urlaub von ${fmtDateDE(confirmStatus.v.start_date)} bis ${fmtDateDE(confirmStatus.v.end_date)} ${confirmStatus.status === "approved" ? "genehmigen" : "stornieren"}?`}
          confirmLabel={confirmStatus.status === "approved" ? "Genehmigen" : "Stornieren"}
          onConfirm={() => { const c = confirmStatus; setConfirmStatus(null); setStatus(c.v, c.status); }}
          onCancel={() => setConfirmStatus(null)}
        />
      )}

    </div>
  );
}

function SickPanel({ user, entries, holidays, viewYear, setViewYear, minYear, maxYear, onChanged, setErr }) {
  const todayIso = useToday();
  const today = useMemo(() => parseISODateLocal(todayIso), [todayIso]);
  const currentYear = today.getFullYear();
  const [editor, setEditor] = useState(null); // {originalStart, originalEnd, start, end} or null
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  // Range picking: false = next calendar click starts a new range, true = it sets the end.
  const [pickEnd, setPickEnd] = useState(false);
  const pickRangeDay = (y, m, d) => {
    const iso = `${y}-${pad2(m + 1)}-${pad2(d)}`;
    setEditor((s) => {
      if (!s) return s;
      if (!pickEnd) return { ...s, start: iso, end: iso };
      if (iso < s.start) return { ...s, start: iso };
      return { ...s, end: iso };
    });
    setPickEnd((p) => !p);
  };

  // Half vs. full day is measured against that day's Soll (see isHalfSickEntry).
  const isHalfEntry = useCallback((e) => isHalfSickEntry(user, e), [user]);

  const entryById = useMemo(() => {
    const m = new Map();
    for (const e of entries) m.set(e.id, e);
    return m;
  }, [entries]);

  // Group consecutive krank entries (by date) into ranges; also remember the
  // underlying entry IDs per day so we can delete/replace surgically. Each
  // range is either all-full or all-half; we tag it via the first entry.
  const allRanges = useMemo(() => {
    const byDate = new Map();
    for (const e of entries) {
      const iso = formatISODate(new Date(e.started_at));
      if (!byDate.has(iso)) byDate.set(iso, []);
      byDate.get(iso).push(e.id);
    }
    const dates = [...byDate.keys()].sort();
    const ranges = [];
    let cur = null;
    for (const iso of dates) {
      const ids = byDate.get(iso);
      const half = isHalfEntry(entryById.get(ids[0]));
      if (!cur) {
        cur = { start: iso, end: iso, ids: [...ids], half };
      } else {
        // parseISODateLocal, not new Date("YYYY-MM-DD") — the latter parses as
        // UTC midnight and only works by luck in UTC+X timezones.
        const next = addDays(parseISODateLocal(cur.end), 1);
        if (formatISODate(next) === iso && cur.half === half) {
          cur.end = iso;
          cur.ids.push(...ids);
        } else {
          ranges.push(cur);
          cur = { start: iso, end: iso, ids: [...ids], half };
        }
      }
    }
    if (cur) ranges.push(cur);
    return ranges;
  }, [entries, entryById, isHalfEntry]);

  // Year navigation is shared across all tabs (bounds come from the parent).
  const canPrev = viewYear > minYear;
  const canNext = viewYear < maxYear;

  const yearStart = `${viewYear}-01-01`;
  const yearEnd = `${viewYear}-12-31`;
  const rangesInYear = allRanges.filter((r) => r.start <= yearEnd && r.end >= yearStart);

  // count weekdays minus holidays in [start, end] that fall in viewYear
  const daysIn = (startISO, endISO) => {
    const [sy, sm, sd] = startISO.split("-").map(Number);
    const [ey, em, ed] = endISO.split("-").map(Number);
    let cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    let n = 0;
    while (cur <= end) {
      if (cur.getFullYear() === viewYear) {
        const wd = cur.getDay();
        if (wd !== 0 && wd !== 6 && !holidays[formatISODate(cur)]) n++;
      }
      cur = addDays(cur, 1);
    }
    return n;
  };

  const totalDays = rangesInYear.reduce(
    (s, r) => s + daysIn(r.start, r.end) * (r.half ? 0.5 : 1),
    0
  );

  const openCreate = () => {
    const iso = viewYear === currentYear ? formatISODate(today) : `${viewYear}-01-01`;
    setPickEnd(false);
    setEditor({ originalIds: [], start: iso, end: iso, half: false });
  };
  const openEdit = (r) => {
    setPickEnd(false);
    setEditor({ originalIds: [...r.ids], start: r.start, end: r.end, half: !!r.half });
  };
  const closeEditor = () => { if (!saving) setEditor(null); };

  const saveEditor = async () => {
    if (!editor) return;
    if (editor.end < editor.start) {
      setErr && setErr("Ende darf nicht vor dem Start liegen.");
      return;
    }
    // A half day only makes sense for a single-day entry.
    const halfDay = !!editor.half && editor.start === editor.end;
    setSaving(true);
    try {
      // CREATE the new entries first, THEN delete the old ones — the other way
      // round a network failure mid-save silently erased the sick days with
      // nothing written yet. A brief double-count between the two steps is the
      // lesser evil; a failure now leaves at worst both versions to clean up.
      const [sy, sm, sd] = editor.start.split("-").map(Number);
      const [ey, em, ed] = editor.end.split("-").map(Number);
      let cur = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      while (cur <= end) {
        const wd = cur.getDay();
        const iso = formatISODate(cur);
        // Weekends AND public holidays are skipped — a holiday already credits the
        // day on its own; a sick entry there would count the day twice (the display
        // and daysIn skip holidays too, and the backend now rejects such entries).
        if (wd !== 0 && wd !== 6 && !holidays[iso]) {
          // Credit the user's actual per-day Soll (half day → half of it) instead of
          // a fixed 8 h / 4 h block. Days that aren't a workday for this user (Soll 0)
          // get no entry — you can't be sick-off on a day you don't work.
          const creditMs = sickCreditMs(user, cur, halfDay);
          const startISO = combineDateTime(iso, "09:00");
          // Number.isFinite: ein NaN-Guthaben passiert jeden ">"-Vergleich und
          // haette hier ein ungueltiges Enddatum erzeugt (toISOString wirft).
          if (Number.isFinite(creditMs) && creditMs > 0 && startISO) {
            await createTimeEntry({
              user_id: user.id,
              started_at: startISO,
              ended_at: new Date(new Date(startISO).getTime() + creditMs).toISOString(),
              kind: "sick",
              description: "",
            });
          }
        }
        cur = addDays(cur, 1);
      }
      // New entries are in — now remove the replaced originals.
      for (const id of editor.originalIds) {
        await deleteTimeEntry(id);
      }
      setEditor(null);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
      setErr && setErr(e?.detail || e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const deleteFromEditor = async () => {
    if (!editor || editor.originalIds.length === 0) return;
    setSaving(true);
    try {
      for (const id of editor.originalIds) {
        await deleteTimeEntry(id);
      }
      setEditor(null);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
      setErr && setErr(e?.detail || e?.message || "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="emp-vacation">
      <div className="wf-vac-year-nav">
        <button type="button" className="wf-nav-btn" onClick={() => canPrev && setViewYear(viewYear - 1)} disabled={!canPrev} title="Vorheriges Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <span className="wf-vac-year-label">{viewYear}</span>
        <button type="button" className="wf-nav-btn" onClick={() => canNext && setViewYear(viewYear + 1)} disabled={!canNext} title="Nächstes Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        <button type="button" className="wf-nav-btn emp-sick-add emp-add-after-year" onClick={openCreate} title="Krankheit hinzufügen">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
        <span className="emp-sick-total emp-sick-total-right">{fmtNumDot(totalDays)}<span className="emp-sick-total-unit">{totalDays === 1 ? "Tag" : "Tage"}</span></span>
      </div>

      <div className="wf-vac-list emp-sick-list">
        <div className="emp-sick-row emp-sick-row-head">
          <span>Von</span>
          <span>Bis</span>
          <span className="emp-sick-head-days">Tage</span>
        </div>
        {rangesInYear.length === 0 && (
          <div className="wf-empty-week emp-sick-empty">Keine Krankheitstage in {viewYear}.</div>
        )}
        {rangesInYear.map((r) => (
          <div key={`${r.start}-${r.end}`} className="emp-sick-row emp-sick-row-clickable" onClick={(e) => { e.stopPropagation(); openEdit(r); }} title="Bearbeiten">
            <span className="wf-vac-cell wf-vac-cell-muted">{fmtDateDE(r.start)}</span>
            <span className="wf-vac-cell wf-vac-cell-muted">{fmtDateDE(r.end)}</span>
            <span className="wf-vac-cell wf-vac-cell-muted">{fmtDays(daysIn(r.start, r.end) * (r.half ? 0.5 : 1))}</span>
          </div>
        ))}
      </div>

      {editor && (
        <div className="modal-overlay" onMouseDown={closeEditor}>
          <div className="modal-content emp-entry-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editor.originalIds.length > 0 ? "Krankheit bearbeiten" : "Neue Krankheit"}
              </h2>
              <button className="modal-close-btn" onClick={closeEditor} disabled={saving}>&times;</button>
            </div>
            <div className="emp-range-cal emp-range-cal-solo">
              <MiniCalendar
                currentYear={Number(editor.start.slice(0, 4))}
                currentMonth={Number(editor.start.slice(5, 7)) - 1}
                onSelect={pickRangeDay}
                rangeStart={editor.start}
                rangeEnd={editor.end}
                hideToday
              />
            </div>
            <div className="wf-form-actions emp-sick-actions">
              <div className="wf-form-actions-left">
                {editor.originalIds.length > 0 && (
                  <button type="button" className="btn btn-danger" onClick={() => setConfirmDel(true)} disabled={saving}>Löschen</button>
                )}
              </div>
              {editor.start === editor.end ? (
                <label className="emp-halfday-toggle emp-halfday-center">
                  <input
                    type="checkbox"
                    checked={!!editor.half}
                    onChange={(e) => setEditor((s) => ({ ...s, half: e.target.checked }))}
                  />
                  <span>Halber Tag</span>
                </label>
              ) : <span />}
              <div className="wf-form-actions-right">
                <button type="button" className="btn btn-primary" onClick={saveEditor} disabled={saving}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDel && editor && (
        <ConfirmDialog
          message={`Krankheitstage von ${fmtDateDE(editor.start)} bis ${fmtDateDE(editor.end)} löschen?`}
          onConfirm={async () => { setConfirmDel(false); await deleteFromEditor(); }}
          onCancel={() => setConfirmDel(false)}
        />
      )}
    </div>
  );
}

function HoursAccountPanel({ user, entries, vacations, holidays, semesterBreaks, viewYear, setViewYear, minYear, maxYear, onSaveUser, projects }) {
  const todayIso = useToday();
  const today = useMemo(() => parseISODateLocal(todayIso), [todayIso]);
  const weeklyHoursTarget = user?.weekly_hours ?? 40;

  const holidayEntries = useMemo(
    () => buildHolidayEntries(user, holidays),
    [holidays, user]
  );

  const vacationEntries = useMemo(
    () => buildVacationEntries(user, vacations, holidays),
    [vacations, holidays, user]
  );


  return (
    <HoursAccountYearView
      entries={entries}
      holidayEntries={holidayEntries}
      vacationEntries={vacationEntries}
      weeklyHoursTarget={weeklyHoursTarget}
      today={today}
      user={user}
      hideTotalUnit
      onSaveUser={onSaveUser}
      semesterBreaks={semesterBreaks}
      viewYear={viewYear}
      setViewYear={setViewYear}
      minYear={minYear}
      maxYear={maxYear}
      projects={projects}
    />
  );
}

const WORKDAYS = [
  { v: "1", l: "Mo" },
  { v: "2", l: "Di" },
  { v: "3", l: "Mi" },
  { v: "4", l: "Do" },
  { v: "5", l: "Fr" },
  { v: "6", l: "Sa" },
  { v: "7", l: "So" },
];

// Parse fixed_workdays string ("1,2,3h,4,5") → Map<day, "full" | "half">.
// "h"-suffixed entries (e.g. "3h") are half days; bare digits are full days.
function parseWorkdays(str) {
  const map = new Map();
  for (const raw of (str || "").split(",").map((s) => s.trim()).filter(Boolean)) {
    const isHalf = /h$/i.test(raw);
    const day = isHalf ? raw.slice(0, -1) : raw;
    if (!day) continue;
    map.set(day, isHalf ? "half" : "full");
  }
  return map;
}

function encodeWorkdays(map) {
  // Re-encode in fixed weekday order (Mo..So).
  return WORKDAYS.map((d) => {
    const v = map.get(d.v);
    if (v === "full") return d.v;
    if (v === "half") return `${d.v}h`;
    return null;
  }).filter(Boolean).join(",");
}

function WorkdayChips({ value, onChange }) {
  const selected = parseWorkdays(value);
  // Cycle: none → half → full → none
  const cycle = (v) => {
    const next = new Map(selected);
    const cur = next.get(v);
    if (!cur) next.set(v, "half");
    else if (cur === "half") next.set(v, "full");
    else next.delete(v);
    onChange(encodeWorkdays(next));
  };
  return (
    <div className="emp-workday-chips">
      {WORKDAYS.map((d) => {
        const state = selected.get(d.v); // undefined | "half" | "full"
        const cls = state === "full" ? "is-on is-full" : state === "half" ? "is-on is-half" : "";
        const title = state === "half" ? "Halber Tag — Klick für ganzen Tag" : state === "full" ? "Ganzer Tag — Klick für aus" : "Aus — Klick für halben Tag";
        return (
          <button
            key={d.v}
            type="button"
            className={`emp-workday-chip ${cls}`}
            title={title}
            onClick={() => cycle(d.v)}
          >
            {d.l}
          </button>
        );
      })}
    </div>
  );
}
